import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  DollarSign, 
  Users, 
  FileText, 
  TrendingUp,
  AlertTriangle,
  Eye,
  CheckSquare,
  XSquare,
  Send,
  BarChart3,
  Calendar,
  Filter,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../utils/axios.ts';
import LoanApprovalQueue from '../components/LoanApprovalQueue';
import ApprovalHistory from '../components/ApprovalHistory';
import LoanStatusTimeline from '../components/LoanStatusTimeline';
import { PermissionPage } from '../components/PermissionGate';
import { PERMISSIONS } from '../utils/permissions';

const LoanApprovalDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);

  // Fetch dashboard statistics
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['loan-approval-dashboard-stats'],
    queryFn: async () => {
      const [pendingResponse, historyResponse, loanStatsResponse] = await Promise.all([
        api.get('/memberloan/pending-approvals?per_page=1'), // Just get count
        api.get('/memberloan/approval-history?per_page=1'), // Just get count
        api.get('/memberloan/statistics')
      ]);
      
      return {
        pendingCount: pendingResponse.data.meta?.total || 0,
        historyCount: historyResponse.data.meta?.total || 0,
        loanStats: loanStatsResponse.data.data
        
      };
    },
    staleTime: 30000, // 30 seconds
  });

  // Fetch recent approvals
  const { data: recentApprovals, isLoading: recentLoading } = useQuery({
    queryKey: ['recent-approvals'],
    queryFn: async () => {
      const response = await api.get('/memberloan/approval-history?per_page=5&status=APPROVED');
      return response.data;
    },
    staleTime: 30000,
  });

  // Fetch pending approvals for quick view
  const { data: pendingLoans, isLoading: pendingLoading } = useQuery({
    queryKey: ['pending-loans-quick'],
    queryFn: async () => {
      const response = await api.get('/memberloan/pending-approvals?per_page=3');
      return response.data;
    },
    staleTime: 30000,
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      'APPROVED': { color: 'bg-green-100 text-green-800', text: 'Approved' },
      'DISAPPROVED': { color: 'bg-red-100 text-red-800', text: 'Disapproved' },
      'ACTIVE': { color: 'bg-blue-100 text-blue-800', text: 'Active' }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const calculateTotalPendingAmount = () => {
    if (!pendingLoans?.data) return 0;
    return pendingLoans.data.reduce((total, loan) => total + loan.principal, 0);
  };

  const getQuickActionCards = () => [
    {
      title: 'Review Pending',
      description: `${dashboardStats?.pendingCount || 0} loans awaiting approval`,
      icon: Clock,
      color: 'bg-yellow-500',
      action: () => setActiveTab('queue'),
      count: dashboardStats?.pendingCount || 0
    },
    {
      title: 'Recent Approvals',
      description: 'View recently approved loans',
      icon: CheckCircle,
      color: 'bg-green-500',
      action: () => setActiveTab('history'),
      count: recentApprovals?.data?.length || 0
    },
    {
      title: 'Approval History',
      description: 'Complete approval history and reports',
      icon: BarChart3,
      color: 'bg-blue-500',
      action: () => setActiveTab('history'),
      count: dashboardStats?.historyCount || 0
    },
    {
      title: 'Loan Statistics',
      description: 'View loan performance metrics',
      icon: TrendingUp,
      color: 'bg-purple-500',
      action: () => setActiveTab('overview'),
      count: dashboardStats?.loanStats?.summary?.total_loans || 0
    }
  ];

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'queue', name: 'Approval Queue', icon: Clock },
    { id: 'history', name: 'Approval History', icon: FileText }
  ];

  return (
    <PermissionPage permission={PERMISSIONS.APPROVE_LOAN}>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Loan Approval Dashboard</h1>
              <p className="text-sm text-gray-600">
                Manage loan applications and approval workflow
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="Refresh"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
              {/* <Link
                to="/loans"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                View All Loans
              </Link> */}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 kpi-grid">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Approvals</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statsLoading ? '...' : dashboardStats?.pendingCount || 0}
                </p>
                <p className="text-sm text-gray-600">
                  {formatCurrency(calculateTotalPendingAmount())}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Approved This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statsLoading ? '...' : dashboardStats?.loanStats?.by_status?.approved?.count || 0}
                </p>
                <p className="text-sm text-gray-600">
                  {formatCurrency(dashboardStats?.loanStats?.by_status?.approved?.total_principal || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Active Loans</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statsLoading ? '...' : dashboardStats?.loanStats?.by_status?.active?.count || 0}
                </p>
                <p className="text-sm text-gray-600">
                  {formatCurrency(dashboardStats?.loanStats?.by_status?.active?.total_balance || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-8 w-8 text-red-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Overdue Loans</p>
                <p className="text-2xl font-bold text-gray-900">
                  {statsLoading ? '...' : dashboardStats?.loanStats?.summary?.overdue_loans || 0}
                </p>
                <p className="text-sm text-gray-600">Requires attention</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {getQuickActionCards().map((card, index) => (
            <button
              key={index}
              onClick={card.action}
              className="bg-white rounded-lg shadow p-6 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center">
                <div className={`flex-shrink-0 p-3 rounded-lg ${card.color}`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-gray-900">{card.title}</p>
                  <p className="text-sm text-gray-500">{card.description}</p>
                  <p className="text-lg font-bold text-gray-900 mt-1">{card.count}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Recent Approvals */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Recent Approvals
                    </h3>
                    {recentLoading ? (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          </div>
                        ))}
                      </div>
                    ) : recentApprovals?.data?.length > 0 ? (
                      <div className="space-y-3">
                        {recentApprovals.data.slice(0, 5).map((loan) => (
                          <div key={loan.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                #{loan.loan_no} - {loan.member?.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {format(new Date(loan.updated_at), 'MMM dd, yyyy HH:mm')}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {getStatusBadge(loan.status)}
                              <button
                                onClick={() => {
                                  setSelectedLoan(loan);
                                  setShowTimeline(true);
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="View Timeline"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No recent approvals</p>
                    )}
                  </div>

                  {/* Pending Loans Quick View */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Clock className="h-5 w-5 text-yellow-500 mr-2" />
                      Pending Approvals
                    </h3>
                    {pendingLoading ? (
                      <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                          </div>
                        ))}
                      </div>
                    ) : pendingLoans?.data?.length > 0 ? (
                      <div className="space-y-3">
                        {pendingLoans.data.slice(0, 5).map((loan) => (
                          <div key={loan.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                #{loan.loan_no} - {loan.member?.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatCurrency(loan.principal)} • {loan.duration} months
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {getStatusBadge(loan.status)}
                              <button
                                onClick={() => setActiveTab('queue')}
                                className="p-1 text-blue-600 hover:text-blue-800"
                                title="Review"
                              >
                                <CheckSquare className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {pendingLoans.data.length > 5 && (
                          <button
                            onClick={() => setActiveTab('queue')}
                            className="w-full mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View all {pendingLoans.data.length} pending loans →
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No pending approvals</p>
                    )}
                  </div>
                </div>

                {/* Loan Statistics Summary */}
                {dashboardStats?.loanStats && (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <TrendingUp className="h-5 w-5 text-purple-500 mr-2" />
                      Loan Statistics Summary
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 kpi-grid">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">
                          {dashboardStats.loanStats.summary?.total_loans || 0}
                        </p>
                        <p className="text-sm text-gray-500">Total Loans</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(dashboardStats.loanStats.summary?.total_principal || 0)}
                        </p>
                        <p className="text-sm text-gray-500">Total Principal</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">
                          {dashboardStats.loanStats.summary?.avg_repayment_rate || 0}%
                        </p>
                        <p className="text-sm text-gray-500">Repayment Rate</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900">
                          {dashboardStats.loanStats.summary?.recent_loans || 0}
                        </p>
                        <p className="text-sm text-gray-500">This Month</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'queue' && (
              <LoanApprovalQueue />
            )}

            {activeTab === 'history' && (
              <ApprovalHistory />
            )}
          </div>
        </div>
      </div>

      {/* Timeline Modal */}
      {showTimeline && selectedLoan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Loan Timeline - #{selectedLoan.loan_no}
                </h2>
                <button
                  onClick={() => {
                    setShowTimeline(false);
                    setSelectedLoan(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
              <LoanStatusTimeline loan={selectedLoan} />
            </div>
          </div>
        </div>
      )}
    </div>
    </PermissionPage>
  );
};

export default LoanApprovalDashboard; 